import React from 'react';
import { useAuth } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { User as UserIcon, LogOut, LogIn, UserPlus, Loader2 } from 'lucide-react';

// Deliberately NOT a map of plan NAMES. Plans can be renamed at any time, and the previous
// name-keyed map silently fell through to the neutral variant for anything it didn't know —
// so a rename would have greyed out every paying user's badge.
function planBadgeVariant(rank: number): 'default' | 'secondary' | 'success' | 'warning' {
  if (rank >= 30) return 'warning';
  if (rank >= 20) return 'success';
  if (rank >= 10) return 'default';
  return 'secondary';
}

export const UserMenu: React.FC = () => {
  const { user, isAuthenticated, isLoading, logout } = useAuth();

  if (isLoading) {
    return (
      <div className="h-8 w-8 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="flex items-center gap-1.5">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2.5 text-xs"
          onClick={() => {
            window.location.hash = '#/login';
          }}
        >
          <LogIn className="h-3.5 w-3.5 mr-1.5" />
          Sign in
        </Button>
        <Button
          variant="default"
          size="sm"
          className="h-8 px-2.5 text-xs"
          onClick={() => {
            window.location.hash = '#/register';
          }}
        >
          <UserPlus className="h-3.5 w-3.5 mr-1.5" />
          Register
        </Button>
      </div>
    );
  }

  const initials = (user.email[0] || '?').toUpperCase();
  const tierVariant = planBadgeVariant(user.planRank ?? 0);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 px-2 py-1 rounded-full bg-muted/50 hover:bg-accent transition-colors">
          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-primary to-blue-600 text-white text-[11px] font-bold flex items-center justify-center">
            {initials}
          </div>
          <span className="hidden md:inline text-xs font-medium max-w-[140px] truncate">
            {user.email}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium truncate">{user.email}</span>
            <div className="flex flex-wrap items-center gap-1">
              <Badge variant={tierVariant} className="text-[10px] px-1.5 py-0">
                {user.planName || user.tier}
              </Badge>
              {user.roles.map((r) => (
                <Badge
                  key={r}
                  variant={r === 'SuperAdmin' ? 'destructive' : 'outline'}
                  className="text-[10px] px-1.5 py-0"
                >
                  {r}
                </Badge>
              ))}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled className="text-xs text-muted-foreground gap-2">
          <UserIcon className="h-3.5 w-3.5" />
          ID: {user.id.slice(0, 8)}…
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            void (async () => {
              await logout();
              // Land on the marketing page, not on a dashboard tab the
              // user is no longer authorised for.
              window.location.hash = '#/';
            })();
          }}
          className="text-destructive focus:text-destructive gap-2"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
